import { useEffect, useId, useState } from 'react';

interface DuplicateDialogProps {
  open: boolean;
  sourceSlug: string;
  sourceName: string;
  busy?: boolean;
  onConfirm: (newSlug: string) => void;
  onCancel: () => void;
}

// Mirrors the server-side regex (DocuSense.Application.Validators.SlugValidator)
// — lowercase letters, digits, hyphens and underscores; 1..60 chars.
const SLUG_REGEX = /^[a-z0-9_-]+$/;
const SLUG_MAX_LENGTH = 60;

interface SlugValidation {
  ok: boolean;
  error: string | null;
}

function validateSlug(candidate: string): SlugValidation {
  const trimmed = candidate.trim();
  if (trimmed === '') {
    return { ok: false, error: 'Indica un slug para el nuevo tipo.' };
  }
  if (trimmed.length > SLUG_MAX_LENGTH) {
    return { ok: false, error: `Máximo ${SLUG_MAX_LENGTH} caracteres.` };
  }
  if (!SLUG_REGEX.test(trimmed)) {
    return {
      ok: false,
      error: 'Solo letras minúsculas, números, guiones (-) y guiones bajos (_).',
    };
  }
  return { ok: true, error: null };
}

/**
 * Schema Designer "Duplicar" modal (Wave 9.B).
 *
 * Visually mirrors `ConfirmDialog`: same overlay, same card,
 * same button row. The only extra is a single slug input that's validated
 * client-side against the same regex the backend's `SlugValidator` uses, so the
 * user gets the same feedback without a network round-trip. The Confirm button
 * stays disabled until the input is valid; on submit we propagate the trimmed
 * value upstream.
 */
export function DuplicateDialog({
  open,
  sourceSlug,
  sourceName,
  busy = false,
  onConfirm,
  onCancel,
}: DuplicateDialogProps): JSX.Element | null {
  const defaultSlug = `${sourceSlug}-copia`;
  const [newSlug, setNewSlug] = useState<string>(defaultSlug);
  const [touched, setTouched] = useState<boolean>(false);
  const inputId = useId();
  const errorId = useId();

  // Reset internal state every time the dialog reopens — otherwise the previous
  // slug bleeds across confirmations.
  useEffect(() => {
    if (open) {
      setNewSlug(`${sourceSlug}-copia`);
      setTouched(false);
    }
  }, [open, sourceSlug]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !busy) {
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) {
    return null;
  }

  const validation = validateSlug(newSlug);
  const showError = touched && !validation.ok;

  const handleConfirm = (): void => {
    if (!validation.ok) {
      setTouched(true);
      return;
    }
    onConfirm(newSlug.trim());
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="duplicate-dialog-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !busy) {
          onCancel();
        }
      }}
    >
      <div className="card w-full max-w-md p-4 shadow-lg sm:p-6">
        <h2 id="duplicate-dialog-title" className="text-lg font-semibold text-slate-900">
          Duplicar tipo de documento
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Se creará un tipo nuevo a partir de{' '}
          <span className="font-mono font-semibold">{sourceSlug}</span>{' '}
          <span className="text-slate-500">({sourceName})</span> copiando todos los
          campos, validaciones y umbrales. El nuevo tipo empieza en la versión{' '}
          <span className="font-mono">1.0.0</span>; el slug debe estar libre.
        </p>

        <div className="mt-4">
          <label className="label" htmlFor={inputId}>
            Slug del nuevo tipo
          </label>
          <input
            id={inputId}
            type="text"
            className="input font-mono text-sm"
            value={newSlug}
            onChange={(event) => {
              setNewSlug(event.target.value);
              if (!touched) {
                setTouched(true);
              }
            }}
            onBlur={() => setTouched(true)}
            disabled={busy}
            maxLength={SLUG_MAX_LENGTH}
            aria-invalid={showError}
            aria-describedby={showError ? errorId : undefined}
            placeholder={defaultSlug}
          />
          {showError ? (
            <p id={errorId} className="mt-1 text-xs text-rose-700">
              {validation.error}
            </p>
          ) : (
            <p className="mt-1 text-xs text-slate-500">
              Letras minúsculas, números, guiones (-) y guiones bajos (_). Máximo{' '}
              {SLUG_MAX_LENGTH} caracteres.
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={onCancel}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn bg-primary text-white hover:bg-primary-hover focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={handleConfirm}
            disabled={busy || !validation.ok}
          >
            {busy ? 'Duplicando…' : 'Duplicar'}
          </button>
        </div>
      </div>
    </div>
  );
}
